import { FormGroup, FormGroupFeedback, ISelectOption, Intent } from '@pinpointhq/thumbtack';
import * as React from 'react';
import Select from 'react-select';
import { useCustomEventEffect } from '../../hooks';
import { sanitizeContent } from '../../thumbtack';
import toSentence from '../../utilities/toSentence';
import { HiddenInputs } from './shared/HiddenInputs';
import {
  IAnswerMatchParams,
  IQuestionAnswerUpdateEventDetails,
  IQuestionDetails,
  matchQuestionConditions,
} from './shared/utilities';

interface IMultiSelect {
  multipleChoiceHelperText: string;
  questionDetails: IQuestionDetails;
  required: boolean;
  attributeName?: string;
  inputId?: string;
  initialErrors?: string[];
  name: string;
  options: { value: number | string; label: string }[];
  placeholder?: string;
  storedOptions?: (number | string)[];
  conditionalQuestionParentQuestionId: string;
  conditionalQuestionConditions: { operator: string; value: string }[];
  formResourceType: 'application' | 'job_seeker';
}

export default function MultiSelect(props: IMultiSelect) {
  const {
    multipleChoiceHelperText,
    questionDetails,
    attributeName,
    inputId,
    initialErrors = [],
    name,
    options,
    placeholder = 'Select...',
    storedOptions,
    conditionalQuestionConditions,
    conditionalQuestionParentQuestionId,
    formResourceType,
  } = props;
  const { index, questionId, questionType, title, additionalInformation, required } = questionDetails;
  const condition = conditionalQuestionConditions && conditionalQuestionConditions[0];
  const [visible, setVisible] = React.useState<boolean>(conditionalQuestionParentQuestionId === null);
  const [errors, setErrors] = React.useState<string[]>(initialErrors);
  const [selectedOptions, setSelectedOptions] = React.useState<{ label: string; value: string | number }[]>([]);

  /**
   * Sender - announce to possible conditional question answers when the value of this rendered component changes
   */
  const { dispatch: announceAnswerUpdate } = useCustomEventEffect(`question:${questionId}:answer:update`);

  React.useEffect(() => {
    setTimeout(() => {
      announceAnswerUpdate({
        questionType: 'multiple_choice',
        value: options.filter((option) => storedOptions?.includes(option.value)),
      });
    }, 250);
  }, []);

  /**
   * Receiver - receive announcements from possible parent conditional questions about their changing value(s) so
   * we know whether to hide or show this answer component
   */
  useCustomEventEffect(
    `question:${conditionalQuestionParentQuestionId}:answer:update`,
    ({ detail }: CustomEvent<IQuestionAnswerUpdateEventDetails>) => {
      const matchParams: IAnswerMatchParams = {
        questionType: detail.questionType,
        value: detail.value,
        parentQuestionCondition: condition,
        maxNumAnswerOptions: detail.maxNumAnswerOptions,
      };

      // Only hide element if it's got a parent conditional question
      if (conditionalQuestionParentQuestionId) {
        setVisible(matchQuestionConditions(matchParams));
      }
    },
  );

  React.useEffect(() => {
    if (storedOptions) {
      const selected = options.filter((option) => storedOptions.includes(option.value));
      setSelectedOptions(selected);
      announceAnswerUpdate({ questionType: 'multiple_choice', maxNumAnswerOptions: 1, value: selected });
    }
  }, [options, storedOptions]);

  function handleChange(selectedItems: ISelectOption[]) {
    setErrors([]);
    setSelectedOptions(selectedItems);
    announceAnswerUpdate({ questionType: 'multiple_choice', value: selectedItems });
  }

  function selectName(index: number) {
    if (!attributeName) return name;

    return `${name}[${index}][${attributeName}]`;
  }

  if (!visible) return null;

  return (
    <div className="pad-v-3">
      <label
        className="external-form__label"
        htmlFor={`${formResourceType}_form_${formResourceType}_answers_attributes_${index}_text_answer`}
      >
        <span className={`external-form__label--title ${required ? 'external-form__label--required' : ''}`}>
          {title}
        </span>
        {additionalInformation && <span dangerouslySetInnerHTML={{ __html: sanitizeContent(additionalInformation) }} />}
        <div dangerouslySetInnerHTML={{ __html: multipleChoiceHelperText }} />
      </label>
      <Select
        value={selectedOptions}
        options={options}
        name={attributeName ? 'selectBox' : name}
        isMulti={true}
        inputId={inputId || name}
        className={`react-select ${errors.length > 0 ? 'react-select--error' : ''}`}
        classNamePrefix="react-select"
        isSearchable={true}
        closeMenuOnSelect={false}
        onChange={handleChange}
        placeholder={placeholder}
        tabSelectsValue={false}
      />
      {errors.length > 0 && (
        <FormGroup>
          <FormGroupFeedback intent={Intent.DANGER}>{toSentence(initialErrors)}</FormGroupFeedback>
        </FormGroup>
      )}
      {attributeName &&
        selectedOptions.map((option, index) => {
          return <input key={index} type="hidden" name={selectName(index)} value={option.value} />;
        })}
      <HiddenInputs
        formResourceType={formResourceType}
        index={index}
        additionalInformation={additionalInformation}
        title={title}
        questionId={questionId}
        questionType={questionType}
      />
    </div>
  );
}
